// src/components/NotificationCard.jsx
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Handshake, Home, BadgeCheck, Tag, Bell } from 'lucide-react';

/** Converte diversos formatos de data para Date */
function toDate(input) {
  try {
    if (!input) return null;
    if (input instanceof Date) return input;
    // Firestore Timestamp { seconds, nanoseconds }
    if (typeof input === 'object' && input && 'seconds' in input) {
      return new Date(input.seconds * 1000);
    }
    // string ou number
    return new Date(input);
  } catch {
    return null;
  }
}

/** Formata data em pt-BR com fallback */
function formatDate(d) {
  const date = toDate(d);
  if (!date || isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

/** Gera iniciais a partir do nome */
function getInitials(name = '') {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  const first = parts[0][0] || '';
  const second = parts[1]?.[0] || '';
  return (first + second).toUpperCase();
}

const TYPE_LABEL = {
  sociedade: 'Sociedade',
  parceria: 'Parceria',
  'novo-imovel': 'Novo imóvel',
  'venda-imovel': 'Venda',
  'valor-atualizado': 'Valor atualizado',
};

const TYPE_STYLES = {
  sociedade: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  parceria: 'bg-violet-50 text-violet-700 ring-1 ring-violet-200',
  'novo-imovel': 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  'venda-imovel': 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  'valor-atualizado': 'bg-slate-50 text-slate-700 ring-1 ring-slate-200',
  default: 'bg-gray-100 text-gray-700 ring-1 ring-gray-200',
};

function TypeChip({ tipo }) {
  const label = TYPE_LABEL[tipo] ?? tipo ?? 'Notificação';
  const cls = TYPE_STYLES[tipo] ?? TYPE_STYLES.default;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}
    >
      {label}
    </span>
  );
}

/** Avatar com prioridade: foto > iniciais do autor > ícone por tipo */
function Avatar({ tipo, avatarUrl, actorNome }) {
  const [imgFailed, setImgFailed] = useState(false);
  const base =
    'w-10 h-10 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0';

  // 1) Foto
  if (avatarUrl && !imgFailed) {
    return (
      <img
        src={avatarUrl}
        alt={actorNome ? `Foto de ${actorNome}` : 'Foto do autor'}
        className="w-10 h-10 rounded-full object-cover mr-3 mt-1 flex-shrink-0"
        loading="lazy"
        onError={() => setImgFailed(true)}
      />
    );
  }

  // 2) Iniciais do autor
  const initials = actorNome ? getInitials(actorNome) : '';
  if (initials) {
    return (
      <div className={`${base} bg-gray-200 text-gray-700 font-semibold`}>
        {initials}
      </div>
    );
  }

  // 3) Ícone por tipo
  if (tipo === 'sociedade') {
    return (
      <div className={`${base} bg-blue-100 text-blue-700`}>
        <Users size={18} aria-hidden />
      </div>
    );
  }
  if (tipo === 'parceria') {
    return (
      <div className={`${base} bg-violet-100 text-violet-700`}>
        <Handshake size={18} aria-hidden />
      </div>
    );
  }
  if (tipo === 'venda-imovel') {
    return (
      <div className={`${base} bg-amber-100 text-amber-700`}>
        <BadgeCheck size={18} aria-hidden />
      </div>
    );
  }
  if (tipo === 'valor-atualizado') {
    return (
      <div className={`${base} bg-slate-100 text-slate-700`}>
        <Tag size={18} aria-hidden />
      </div>
    );
  }
  if (tipo === 'novo-imovel') {
    return (
      <div className={`${base} bg-emerald-100 text-emerald-700`}>
        <Home size={18} aria-hidden />
      </div>
    );
  }
  return (
    <div className={`${base} bg-gray-100 text-gray-700`}>
      <Bell size={18} aria-hidden />
    </div>
  );
}

export default function NotificationCard({
  tipo,
  mensagem,
  time,
  read = false,
  isPending = false,
  onAccept,
  onReject,
  onMarkRead,
  loading = false,
  link,
  avatarUrl,
  actorNome, // nome de quem fez a ação (opcional)
}) {
  const [localBusy, setLocalBusy] = useState(false);
  const busy = loading || localBusy;

  const dateISO = useMemo(() => {
    const d = toDate(time);
    return d ? d.toISOString() : '';
  }, [time]);

  const action = async (fn) => {
    if (!fn) return;
    try {
      setLocalBusy(true);
      await fn();
    } finally {
      setLocalBusy(false);
    }
  };

  const Wrapper = link ? Link : 'div';
  const wrapperProps = link ? { to: link } : {};

  return (
    <Wrapper
      {...wrapperProps}
      className={[
        'flex items-start bg-white p-4 rounded-lg shadow-sm border transition group',
        link ? 'hover:bg-gray-50 focus:outline-none' : '',
        !read ? 'border-l-4 border-l-blue-500' : '',
      ].join(' ')}
      aria-busy={busy}
      aria-live="polite"
      title={formatDate(time)}
      dateTime={dateISO}
    >
      {/* Avatar (foto / iniciais / ícone por tipo) */}
      <Avatar tipo={tipo} avatarUrl={avatarUrl} actorNome={actorNome} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <TypeChip tipo={tipo} />
            {!read && (
              <span
                className="inline-block w-2 h-2 rounded-full bg-blue-500"
                aria-label="Não lida"
              />
            )}
          </div>
          <span
            className="text-xs text-gray-500 flex-shrink-0"
            aria-label="Data da notificação"
          >
            {formatDate(time)}
          </span>
        </div>

        <p className="text-sm text-gray-800 mt-1 break-words">
          {mensagem || 'Você recebeu uma notificação.'}
        </p>

        {actorNome && (
          <p className="text-xs text-gray-500 mt-0.5">por {actorNome}</p>
        )}

        {/* Ações */}
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {isPending && (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  action(onAccept);
                }}
                className="bg-blue-600 text-white px-3 py-1.5 rounded text-xs hover:bg-blue-700 disabled:opacity-60"
              >
                {busy ? 'Processando...' : 'Aceitar'}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  action(onReject);
                }}
                className="bg-gray-200 text-gray-800 px-3 py-1.5 rounded text-xs hover:bg-gray-300 disabled:opacity-60"
              >
                Recusar
              </button>
            </>
          )}

          {!read && !isPending && onMarkRead && (
            <button
              type="button"
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                action(onMarkRead);
              }}
              className="text-xs text-blue-700 hover:underline disabled:opacity-60"
            >
              Marcar como lida
            </button>
          )}

          {link && (
            <span className="text-xs text-gray-400 ml-auto">Abrir →</span>
          )}
        </div>
      </div>
    </Wrapper>
  );
}
